// AuthorConvertModal — Pre-fills parsed name, shows duplicate warning
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, AlertTriangle, CheckCircle } from 'lucide-react'
import { convertAuthor } from '../../services/api'
import type { ConvertAuthorResponse } from '../../types'

interface Props {
  authorString: string
  sourceDocumentId?: string
  sourceDocumentTitle?: string
  sourceType?: 'paper' | 'patent' | 'journal'
  projectId: string
  onClose: () => void
}

function parseAuthorName(author: string): { first_name: string; last_name: string } {
  if (!author) return { first_name: '', last_name: '' }
  const trimmed = author.trim()

  if (trimmed.includes(',')) {
    const [last, first] = trimmed.split(',', 2).map(s => s.trim())
    return { first_name: first || '', last_name: last || '' }
  }

  const parts = trimmed.split(' ')
  if (parts.length === 1) return { first_name: parts[0], last_name: '' }
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] }
}

export default function AuthorConvertModal({ authorString, sourceDocumentId, sourceDocumentTitle, sourceType, projectId, onClose }: Props) {
  const navigate = useNavigate()
  const parsed = parseAuthorName(authorString)
  const [firstName, setFirstName] = useState(parsed.first_name)
  const [lastName, setLastName] = useState(parsed.last_name)
  const [orgName, setOrgName] = useState('')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<ConvertAuthorResponse | null>(null)
  const [showDuplicate, setShowDuplicate] = useState(false)

  const handleSubmit = async (force = false) => {
    setSaving(true)
    try {
      const resp = await convertAuthor({
        project_id: projectId,
        author_string: `${lastName}, ${firstName}`.trim(),
        source_document_id: sourceDocumentId,
        source_document_title: sourceDocumentTitle,
        source_type: sourceType,
        organisation_name: orgName || undefined,
        force,
      })
      setResult(resp)
      if (resp.duplicate_warning && !force) {
        setShowDuplicate(true)
      }
    } catch (err: unknown) {
      // Check for 409 response
      const axiosErr = err as { response?: { status: number; data: ConvertAuthorResponse } }
      if (axiosErr?.response?.status === 409) {
        setResult(axiosErr.response.data)
        setShowDuplicate(true)
      } else {
        alert('Failed to convert author')
      }
    } finally {
      setSaving(false)
    }
  }

  // Success state
  if (result?.converted) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 text-center" onClick={e => e.stopPropagation()}>
          <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">Contact Created</h3>
          <p className="text-sm text-gray-500 mb-4">
            {result.contact?.first_name} {result.contact?.last_name} has been added to your CRM.
          </p>
          <button onClick={onClose} className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600">Done</button>
        </div>
      </div>
    )
  }

  // Duplicate warning state
  if (showDuplicate && result?.duplicate_warning) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={24} className="text-amber-500" />
              <h3 className="text-base font-semibold text-gray-900">Duplicate Found</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">{result.message}</p>
            {result.existing_contact && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-gray-900">
                  {result.existing_contact.first_name} {result.existing_contact.last_name}
                </p>
                <p className="text-xs text-gray-500">{result.existing_contact.email || result.existing_contact.organisation_name || 'Existing contact'}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => handleSubmit(true)} disabled={saving}
                className="flex-1 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50">
                {saving ? 'Creating...' : 'Create Anyway'}
              </button>
              {/* BUG-106: "View Existing" used to be a plain onClose — a lie.
                  Navigate to the existing contact when we actually can; when
                  the response carries no contact_id, label the button for what
                  it really does. */}
              {result.existing_contact?.contact_id && projectId ? (
                <button
                  onClick={() => {
                    navigate(`/project/${projectId}/crm/contacts/${result.existing_contact!.contact_id}`)
                    onClose()
                  }}
                  className="flex-1 px-4 py-2 border text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  View Existing
                </button>
              ) : (
                <button onClick={onClose} className="flex-1 px-4 py-2 border text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Default form
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">Save as Contact</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {sourceDocumentTitle && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Source</p>
              <p className="text-sm text-gray-700 truncate">{sourceDocumentTitle}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Organisation (optional)</label>
            <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. MIT, Google"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            <button onClick={() => handleSubmit(false)} disabled={saving || (!firstName && !lastName)}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50">
              {saving ? 'Converting...' : 'Save Contact'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
